import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { authService } from '../services/api'

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  loading: true
}

// Action types
const AUTH_SUCCESS = 'AUTH_SUCCESS'
const AUTH_ERROR = 'AUTH_ERROR'
const LOGIN_SUCCESS = 'LOGIN_SUCCESS'
const LOGOUT = 'LOGOUT'
const LOAD_USER = 'LOAD_USER'
const SET_LOADING = 'SET_LOADING'

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload
      }
    case LOAD_USER:
      // For LOAD_USER, we might receive just user data or { token, user }
      if (action.payload.user) {
        // If payload has both token and user
        localStorage.setItem('token', action.payload.token)
        return {
          ...state,
          user: action.payload.user,
          isAuthenticated: true,
          loading: false
        }
      } else {
        // If payload is just user data (token should already be in localStorage)
        return {
          ...state,
          user: action.payload,
          isAuthenticated: true,
          loading: false
        }
      }
    case LOGIN_SUCCESS:
    case AUTH_SUCCESS:
      localStorage.setItem('token', action.payload.token)
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        loading: false
      }
    case AUTH_ERROR:
    case LOGOUT:
      localStorage.removeItem('token')
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false
      }
    default:
      return state
  }
}

// Create context
const AuthContext = createContext()

// Load Google API helper
const loadGoogleAPI = () => {
  return new Promise((resolve, reject) => {
    if (window.google) {
      resolve(window.google)
      return
    }

    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => {
      if (window.google) {
        resolve(window.google)
      } else {
        reject(new Error('Google API failed to load'))
      }
    }
    script.onerror = () => reject(new Error('Failed to load Google API'))
    document.head.appendChild(script)
  })
}

// Error handler
const handleAuthError = (error, defaultMessage = 'An error occurred') => {
  console.error('Auth error:', error)
  
  const message = error.response?.data?.message || error.message || defaultMessage
  const details = error.response?.data?.details
  
  if (details && Array.isArray(details)) {
    details.forEach(detail => toast.error(detail.message))
  } else {
    toast.error(message)
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Load user on app start
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token')
      
      if (token) {
        try {
          dispatch({ type: 'SET_LOADING', payload: true })
          
          const user = await authService.getProfile()
          // Pass both token and user to properly restore the session
          dispatch({ type: 'LOAD_USER', payload: { token, user } })
        } catch (error) {
          console.error('Failed to load user:', error)
          
          // Only clear token if it's definitely invalid, not on network errors
          if (error.response?.status === 401 || error.response?.status === 403) {
            localStorage.removeItem('token')
          } else {
            // For network errors or other issues, don't clear the token
          }
          
          dispatch({ type: 'AUTH_ERROR' })
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    }
    loadUser()
  }, [])

  // Initialize Google API
  useEffect(() => {
    const initGoogleAPI = async () => {
      try {
        await loadGoogleAPI()
      } catch (error) {
        console.error('Failed to load Google API:', error)
      }
    }
    initGoogleAPI()
  }, [])

  const login = async (credentials) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      
      const response = await authService.login(credentials)
      
      dispatch({ type: 'LOGIN_SUCCESS', payload: response })
      toast.success(`Welcome${response.user.name ? `, ${response.user.name}` : ''}!`)
      
      return response
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false })
      handleAuthError(error, 'Login failed')
      throw error
    }
  }

  const register = async (userData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const response = await authService.register(userData)
      dispatch({ type: 'AUTH_SUCCESS', payload: response })
      toast.success(`Registration successful! Welcome${response.user.name ? `, ${response.user.name}` : ''}!`)
      return response
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false })
      handleAuthError(error, 'Registration failed')
      throw error
    }
  }

  const googleLogin = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      
      if (!window.google) {
        await loadGoogleAPI()
      }

      // Clear any existing instances
      try {
        window.google.accounts.id.cancel()
      } catch (e) {
        // No existing Google instance to cancel
      }

      // Create Google OAuth URL for redirect  
      const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${import.meta.env.VITE_GOOGLE_CLIENT_ID}&` +
        `redirect_uri=${encodeURIComponent(window.location.origin + '/auth/google/callback')}&` +
        `response_type=code&` +
        `scope=email profile&` +
        `access_type=online&` +
        `prompt=consent`

      // Redirect to Google OAuth
      window.location.href = googleAuthUrl
      
    } catch (error) {
      console.error('Google login error:', error)
      dispatch({ type: 'SET_LOADING', payload: false })
      
      if (error.message === 'Google API not loaded') {
        toast.error('خدمات Google غير متاحة. يرجى إعادة تحميل الصفحة.')
      } else {
        toast.error('فشل تسجيل الدخول عبر Google. يرجى المحاولة مرة أخرى.')
      }
      
      throw error
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      dispatch({ type: 'LOGOUT' })
      toast.success('Logged out successfully')
    }
  }

  const updateProfile = async (userData) => {
    try {
      const response = await authService.updateProfile(userData)
      dispatch({ type: 'LOAD_USER', payload: response })
      toast.success('Profile updated successfully')
      return response
    } catch (error) {
      handleAuthError(error, 'Failed to update profile')
      throw error
    }
  }

  const value = {
    ...state,
    login,
    register,
    googleLogin,
    logout,
    updateProfile
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
